import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Send, MessageCircle, Smartphone, Activity } from "lucide-react";

const ST = {
  delivered: ["entregue", "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"],
  read: ["lido", "text-emerald-200 bg-emerald-500/20 border-emerald-400/40"],
  sent: ["enviado", "text-sky-300 bg-sky-500/10 border-sky-500/30"],
  failed: ["falhou", "text-red-300 bg-red-500/10 border-red-500/30"],
  queued: ["na fila", "text-slate-300 bg-slate-500/10 border-slate-500/30"],
  mock: ["simulado", "text-slate-400 bg-slate-500/10 border-slate-500/30"],
};
const CH = {
  sms: { label: "SMS", icon: Send, color: "#FBBF24" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  app: { label: "App", icon: Smartphone, color: "#D4FF3A" },
};
const ORDER = ["sms", "whatsapp", "app"];
const hhmm = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const day = (iso) => new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const group = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const k = `${r.client_id || r.to}|${r.rule_id || r.trigger}|${(r.sent_at || "").slice(0, 16)}`;
    if (!map.has(k)) map.set(k, { ...r, key: k, chans: {} });
    map.get(k).chans[r.channel] = r;
  });
  return [...map.values()];
};

const Chip = ({ ch, r }) => {
  const c = CH[ch];
  const st = ch === "whatsapp" && r.wa_status === "read" ? "read" : r.status;
  const [lbl, cls] = ST[st] || [st, ST.queued[1]];
  const Icon = c.icon;
  return (
    <span data-testid={`ops-feed-chip-${ch}-${r.id}`} title={r.error || ""} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" style={{ color: c.color }} />
      <span style={{ color: c.color }}>{c.label}</span>
      <span>{lbl}</span>
    </span>
  );
};

export const LiveFeed = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get("/dispatch/live", { params: { today: true, channel: filter } }).then((r) => setRows(r.data)).catch(() => {}), [filter]);
  useEffect(() => { setRows(null); load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const list = !rows ? [] : group(rows);
  return (
    <div data-testid="ops-live-feed" className="rounded-2xl border border-[#1F293D] bg-[#111827] p-5 fade-up">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200"><Activity className="h-4 w-4 text-emerald-400" /> Monitoramento de envios
          <span data-testid="ops-feed-count" className="text-[11px] font-normal normal-case tracking-normal text-slate-500">{rows ? `${rows.length} envios hoje` : "carregando..."}</span></h3>
        <div className="flex gap-1">
          {[["", "Todos"], ["sms", "SMS"], ["whatsapp", "WhatsApp"], ["app", "App"]].map(([k, l]) => (
            <button key={k || "all"} data-testid={`ops-feed-filter-${k || "all"}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "bg-sky-500 text-slate-950" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="max-h-[420px] divide-y divide-[#1F293D] overflow-y-auto pr-1">
        {list.map((g) => (
          <div key={g.key} data-testid={`ops-feed-row-${g.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <span className="w-20 shrink-0 font-mono text-[11px] text-slate-500">{day(g.sent_at)} {hhmm(g.sent_at)}</span>
            <span className="min-w-0 flex-1 truncate text-slate-200">{g.client_name || g.to}<span className="ml-2 text-xs text-slate-500">{g.rule_name}</span></span>
            <span className="flex shrink-0 flex-wrap gap-1.5">
              {ORDER.filter((ch) => g.chans[ch]).map((ch) => <Chip key={ch} ch={ch} r={g.chans[ch]} />)}
            </span>
          </div>
        ))}
        {!rows && <div data-testid="ops-feed-loading" className="py-8 text-center text-xs text-slate-500">Carregando...</div>}
        {rows && !list.length && <div data-testid="ops-feed-empty" className="py-8 text-center text-xs text-slate-500">Nenhum envio hoje.</div>}
      </div>
    </div>
  );
};
